import { NgModule } from '@angular/core';
import { ExtraOptions, RouteReuseStrategy, RouterModule, Routes } from '@angular/router';
import { BusinessSwitchRouteReuseStrategy } from './_core/state/business';
import { businessContextGuard } from './_core/guards/business-context.guard';
import { AdEditorGuard } from './_core/guards/ad-editor.guard';
import { ContentPageResolver } from './_core/resolvers/content-page.resolver';
import { FileLibraryEnabledGuard, HomeRouteGuard } from './_core/guards/file-library-start.guard';
import { oauthAuthorizationGuard } from './pages/oauth-authorization/oauth-authorization.guard';

/**
 * Exported so the route restructure can be asserted against the real config
 * rather than a copy — see `pages/project/project.page.shell-mode.spec.ts`.
 * `AppRoutingModule` below is still the only consumer in application code.
 */
export const routes: Routes = [
	{
		path: '',
		pathMatch: 'full',
		canActivate: [HomeRouteGuard],
		loadChildren: () => import('./pages/projects/projects.module').then(m => m.ProjectsPageModule)
	},
	// Need fallback so we return to starting folders
	{
		path: 'dashboard',
		canActivate: [FileLibraryEnabledGuard],
		data: { businessSwitchFallback: '/dashboard' },
		loadChildren: () => import('./pages/file-library/file-library.routes').then(m => m.FILE_LIBRARY_ROUTES)
	},
	{
		path: 'file-library',
		canActivate: [FileLibraryEnabledGuard],
		data: { businessSwitchFallback: '/file-library' },
		loadChildren: () => import('./pages/file-library/file-library.routes').then(m => m.FILE_LIBRARY_ROUTES)
	},
	{ path: 'projects', redirectTo: '/dashboard/projects', pathMatch: 'full' },
	{ path: 'login', data: { animationKey: 'login' }, loadChildren: () => import('./pages/auth/auth.module').then(m => m.AuthPageModule) },
	{
		path: 'signup',
		data: { animationKey: 'signup' },
		loadChildren: () => import('./pages/auth/auth.module').then(m => m.AuthPageModule)
	},
	{
		path: 'create-business',
		data: { animationKey: 'create-business' },
		loadChildren: () => import('./pages/auth/auth.module').then(m => m.AuthPageModule)
	},
	{
		path: 'join-business',
		data: { animationKey: 'join-business' },
		loadChildren: () => import('./pages/auth/auth.module').then(m => m.AuthPageModule)
	},
	{
		path: 'business-subscription/:id',
		data: { animationKey: 'business-subscription' },
		loadChildren: () => import('./pages/auth/auth.module').then(m => m.AuthPageModule)
	}, //
	{
		path: 'business-trial/:id',
		data: { animationKey: 'business-trial' },
		loadChildren: () => import('./pages/auth/auth.module').then(m => m.AuthPageModule)
	},
	{
		path: 'validate-subscription/:id',
		data: { animationKey: 'validate-subscription' },
		loadChildren: () => import('./pages/auth/auth.module').then(m => m.AuthPageModule)
	},
	{
		path: 'setup-subscription/:id',
		data: { animationKey: 'setup-subscription' },
		loadChildren: () => import('./pages/auth/auth.module').then(m => m.AuthPageModule)
	},

	{ path: 'project', redirectTo: '', pathMatch: 'full' },
	// The project page renders inside the File Library shell without changing its
	// URL: `loadComponent` supplies the layout parent and `loadChildren` supplies
	// this route's own children, so `ProjectPageModule`'s routes become the
	// shell's children under the unchanged `project/:id` path. Only
	// `component`+`loadComponent` and `children`+`loadChildren` are rejected by
	// the router's config validator; this pair is legal.
	//
	// Deliberately no `FileLibraryEnabledGuard` and no `NoGuestGuard` here. The
	// shell renders a sidebar only when `isSidebarEligible` says so
	// (`pages/file-library/models/file-library-visibility.model.ts`), so a guest
	// or a site with the file library switched off still gets the page —
	// guarding this route would redirect them off it instead. `businessContextGuard`
	// and the business-switch fallback stay exactly where they were: the fallback
	// resolver walks to the deepest child and the last declared value wins, and
	// this node still declares its own.
	{
		path: 'project/:id',
		canActivate: [businessContextGuard],
		data: { businessSwitchFallback: '/' },
		loadComponent: () => import('./pages/file-library/file-library-shell.component').then(m => m.FileLibraryShellComponent),
		loadChildren: () => import('./pages/project/project.module').then(m => m.ProjectPageModule)
	},
	// Not wrapped, on purpose. External reviewers hold `BusinessRole.Guest`
	// alongside `isApprover`, so the shared sidebar predicate excludes them and a
	// shell here would render chrome with nothing in it — and would issue the
	// sidebar's fetches on a shared review link.
	{
		path: 'review/:id',
		canActivate: [businessContextGuard],
		data: { businessSwitchFallback: '/' },
		loadChildren: () => import('./pages/project/project.module').then(m => m.ProjectPageModule)
	},
	{ path: 'admin', loadChildren: () => import('./pages/admin/admin.module').then(m => m.AdminPageModule) },
	{ path: 'settings', loadChildren: () => import('./pages/settings/settings.module').then(m => m.SettingsPageModule) },
	{ path: 'share', loadChildren: () => import('./pages/share/share.module').then(m => m.ShareModule) },
	{ path: 'widget', loadChildren: () => import('./pages/widget/widget.module').then(m => m.WidgetModule) },
	{ path: 'creator', loadChildren: () => import('./pages/creator/creator.module').then(m => m.CreatorPageModule) },
	{ path: 'onboard/external-payment-method', redirectTo: '/?onboard=external-payment-method', pathMatch: 'full' },
	{
		path: 'ad-editor',
		loadChildren: () => import('./pages/sandbox/ad-editor/ad-editor.module').then(m => m.AdEditorPageModule),
		canActivate: [AdEditorGuard]
	},
	{
		path: 'sandbox/ad-editor',
		loadChildren: () => import('./pages/sandbox/ad-editor/ad-editor.module').then(m => m.AdEditorPageModule),
		canActivate: [AdEditorGuard]
	},
	{
		path: 'sandbox/ad-editor/:id',
		loadChildren: () => import('./pages/sandbox/ad-editor/ad-editor.module').then(m => m.AdEditorPageModule),
		canActivate: [AdEditorGuard]
	},
	{
		path: 'sandbox/creative-unit-chat',
		loadChildren: () => import('./pages/sandbox/creative-unit-chat/creative-unit-chat.module').then(m => m.CreativeUnitChatPageModule),
		canActivate: [AdEditorGuard]
	},
	{
		path: 'sandbox/agent-chat',
		loadChildren: () => import('./pages/sandbox/agent-chat/agent-chat-sandbox.module').then(m => m.AgentChatSandboxPageModule),
		canActivate: [AdEditorGuard]
	},
	{
		path: 'sandbox/ai',
		loadChildren: () => import('./pages/sandbox/ai/ai-sandbox.module').then(m => m.AiSandboxPageModule),
		canActivate: [AdEditorGuard]
	},
	{
		path: 'sandbox/creative-x',
		loadChildren: () => import('./pages/sandbox/creative-x/creative-x-sandbox.module').then(m => m.CreativeXSandboxModule),
		canActivate: [AdEditorGuard]
	},
	{
		path: 'sandbox/scripts',
		loadChildren: () => import('./pages/sandbox/scripts/sandbox.module').then(m => m.SandboxModule),
		canActivate: [AdEditorGuard]
	},
	{
		path: 'editor/preview/:id',
		loadChildren: () => import('./pages/sandbox/ad-editor-preview/ad-editor-preview.module').then(m => m.AdEditorPreviewPageModule),
		canActivate: [AdEditorGuard]
	},
	{
		path: 'sandbox/ad-generation',
		loadChildren: () => import('./pages/sandbox/ad-generation/ad-generation.module').then(m => m.AdGenerationPageModule),
		canActivate: [AdEditorGuard]
	},
	{
		path: 'sandbox/merge-tags-test',
		loadChildren: () => import('./pages/sandbox/merge-tags-test/merge-tags-test.module').then(m => m.MergeTagsTestModule)
	},
	{
		path: 'sandbox/figma-import',
		loadChildren: () => import('./pages/sandbox/figma-import/figma-import.module').then(m => m.FigmaImportPageModule),
		canActivate: [AdEditorGuard]
	},
	{
		path: 'sandbox/figma-import/callback',
		loadChildren: () => import('./pages/figma/callback/figma-callback.module').then(m => m.FigmaCallbackModule)
	},
	{
		path: 'sandbox/figma-import-studio',
		loadChildren: () => import('./pages/sandbox/figma-import/figma-import-studio.module').then(m => m.FigmaImportStudioModule),
		canActivate: [AdEditorGuard]
	},
	{
		path: 'sandbox/input-tests',
		loadChildren: () => import('./pages/sandbox/input-tests/input-tests.module').then(m => m.InputTestsPageModule)
	},
	{
		path: 'sandbox/focal-area-cropping',
		loadChildren: () =>
			import('./pages/sandbox/focal-area-cropping/focal-area-cropping-sandbox.module').then(m => m.FocalAreaCroppingSandboxPageModule)
	},
	{
		path: 'sandbox/widget-preview-test',
		loadChildren: () => import('./pages/sandbox/widget-preview-test/widget-preview-test.module').then(m => m.WidgetPreviewTestModule),
		canActivate: [AdEditorGuard]
	},
	{
		path: 'sandbox/mcp-apps-test',
		loadChildren: () => import('./pages/sandbox/mcp-apps-test/mcp-apps-test.module').then(m => m.McpAppsTestModule),
		canActivate: [AdEditorGuard]
	},
	{
		path: 'sandbox/package-api',
		loadChildren: () => import('./pages/sandbox/package-api/package-api.module').then(m => m.PackageApiPageModule),
		canActivate: [AdEditorGuard]
	},
	{
		path: 'editor',
		loadChildren: () => import('./pages/sandbox/ad-editor/ad-editor.module').then(m => m.AdEditorPageModule),
		canActivate: [AdEditorGuard]
	},
	{
		path: 'editor/:id/:creativeUnitId',
		loadChildren: () => import('./pages/sandbox/ad-editor/ad-editor.module').then(m => m.AdEditorPageModule),
		canActivate: [AdEditorGuard]
	},
	{
		path: 'qa/visual-diff',
		loadChildren: () => import('./pages/qa/visual-diff/visual-diff.module').then(m => m.VisualDiffModule),
		canActivate: [AdEditorGuard]
	},
	{
		path: 'oauth/authorize/:transaction',
		canActivate: [oauthAuthorizationGuard],
		loadComponent: () => import('./pages/oauth-authorization/oauth-authorization.component').then(m => m.OAuthAuthorizationComponent)
	},
	// 404 route - must come before wildcard
	{
		path: '404',
		loadComponent: () => import('./pages/not-found/not-found.component').then(m => m.NotFoundComponent)
	},
	// Wildcard route for dynamic content pages - must be last
	{
		path: '**',
		loadComponent: () => import('./pages/dynamic-content-page/dynamic-content-page.component').then(m => m.DynamicContentPageComponent),
		resolve: { page: ContentPageResolver },
		data: { dynamic: true }
	}
];

/**
 * Exported alongside `routes` so the spec can assert the options the app really
 * runs with — `paramsInheritanceStrategy` is load-bearing and invisible in the
 * route array itself.
 */
export const ROUTER_OPTIONS: ExtraOptions = {
	// The shell gives `project/:id` a component, so under the default `emptyOnly`
	// rule its non-empty children (ad-studio, export, add, review) stopped
	// inheriting `:id` and read it as undefined — the project page then bounced
	// to the site root on every one of them.
	paramsInheritanceStrategy: 'always',
	scrollOffset: [0, 0],
	scrollPositionRestoration: 'enabled',
	// Required so a same-URL business switch (e.g. staying on the projects
	// list) re-runs the route instead of being ignored.
	onSameUrlNavigation: 'reload'
};

@NgModule({
	imports: [RouterModule.forRoot(routes, ROUTER_OPTIONS)],
	providers: [{ provide: RouteReuseStrategy, useClass: BusinessSwitchRouteReuseStrategy }],
	exports: [RouterModule]
})
export class AppRoutingModule {}
